import React, { useState, useEffect } from "react";
import axios from "axios";

function TodayThought() {
  const [thought, setThought] = useState("");
  const [author, setAuthor] = useState("");
  const [thoughts, setThoughts] = useState([]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Retrieve sessionId from localStorage
  const sessionId = localStorage.getItem("currentSessionId");

  useEffect(() => {
    // If no session is found, prompt the user to select a session
    if (!sessionId) {
      alert("Session ID not found. Please select a session.");
      return;
    }

    // Fetch thoughts associated with the current session
    fetchThoughts();
  }, [sessionId]);

  // Fetch thoughts based on sessionId
  const fetchThoughts = async () => {
    if (!sessionId) return; // Ensure sessionId exists

    try {
      const res = await axios.get(` ${process.env.REACT_APP_API_BASE_URL}/api/thoughts/getThoughts/${sessionId}`);
      setThoughts(res.data);
    } catch (error) {
      console.error("Failed to load thoughts", error);
      alert("Failed to load thoughts.");
    } finally {
      setIsLoading(false);
    }
  };

  // Submit thought
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!thought.trim() || !author.trim()) {
      return alert("Both thought and author are required.");
    }

    if (!sessionId) {
      alert("Session not selected. Please select a session.");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/thoughts/createThought`, {
        thought,
        author,
        sessionId, // Pass sessionId to associate the thought with the session
      });

      alert("Thought shared successfully!");

      setThought("");
      setAuthor("");

      fetchThoughts(); // Refresh data after adding a new thought
    } catch (err) {
      console.error("Failed to submit thought", err);
      alert("Failed to share thought.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete thought
  const deleteThought = async (id) => {
    if (!sessionId) return;

    try {
      await axios.delete(` ${process.env.REACT_APP_API_BASE_URL}/api/thoughts/deleteThought/${id}`);
      alert("Thought deleted successfully.");
      fetchThoughts(); // Refresh data after deletion
    } catch (err) {
      console.error("Failed to delete thought", err);
      alert("Failed to delete thought.");
    }
  };

  return (
    <div className="container mx-auto p-6 bg-white shadow-lg rounded-lg">
      <h1 className="text-2xl font-bold mb-6">Today's Thoughts</h1>
      <hr />
      <br />

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="flex flex-col">
          <label className="text-gray-700 font-semibold mb-1">Thought</label>
          <textarea
            className="border rounded-lg px-3 py-3 h-28 outline-none focus:ring-2 focus:ring-blue-500"
            value={thought}
            onChange={(e) => setThought(e.target.value)}
            placeholder="Write today's thought..."
          />
        </div>

        <div className="flex flex-col">
          <label className="text-gray-700 font-semibold mb-1">Written By</label>
          <input
            type="text"
            className="border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500"
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
            placeholder="Enter author name"
          />
        </div>

        <div className="flex justify-start gap-10">
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg"
          >
            {isSubmitting ? "Sending..." : "Submit"}
          </button>

          <button
            type="button"
            onClick={() => {
              setThought("");
              setAuthor("");
            }}
            className="bg-red-600 hover:bg-red-400 text-white px-4 py-2 rounded-lg"
          >
            Reset
          </button>
        </div>
      </form>

      <h2 className="text-xl font-bold mt-12 mb-6">Thoughts for Today</h2>
      <hr />
      <br />

      {isLoading ? (
        <p className="text-center text-gray-600">Loading thoughts...</p>
      ) : thoughts.length === 0 ? (
        <p className="text-center text-gray-600">No thoughts shared today.</p>
      ) : (
        <div className="space-y-4">
          {thoughts.map((item) => (
            <div key={item._id} className="bg-gray-50 p-6 border rounded-lg shadow">
              <div className="flex justify-between items-center mb-2">
                <p className="text-xl font-semibold text-gray-800">{item.author}</p>
                <span className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-full">
                  {new Date(item.createdAt).toLocaleDateString()}
                </span>
              </div>

              <p className="text-gray-700 text-lg">{item.thought}</p>

              <button
                onClick={() => deleteThought(item._id)}
                className="mt-3 text-red-600 hover:text-red-700"
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default TodayThought;